import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'schedule-trigger',
  version: '1.1.0',
  properties: [
    {
      key: 'triggerTimes',
      label: 'Trigger Times',
      type: 'fixedCollection',
      typeOptions: {
        multipleValues: true,
      },
      description: 'Define when the workflow should be triggered',
      default: {},
      fixedCollectionOptions: [
        {
          name: 'item',
          displayName: 'Schedule',
          values: [
            {
              key: 'mode',
              label: 'Mode',
              type: 'options',
              default: 'everyHour',
              description: 'How often to trigger',
              options: [
                {
                  name: 'Every X Minutes',
                  value: 'everyXMinutes',
                },
                {
                  name: 'Every Hour',
                  value: 'everyHour',
                },
                {
                  name: 'Every X Hours',
                  value: 'everyXHours',
                },
                {
                  name: 'Every Day',
                  value: 'everyDay',
                },
                {
                  name: 'Every Week',
                  value: 'everyWeek',
                },
                {
                  name: 'Every Month',
                  value: 'everyMonth',
                },
                {
                  name: 'Custom (Cron)',
                  value: 'custom',
                },
              ],
            },
            // Every X Minutes
            {
              key: 'minuteInterval',
              label: 'Every',
              type: 'options',
              default: 15,
              description: 'Trigger every X minutes',
              options: [
                { name: '5 minutes', value: 5 },
                { name: '10 minutes', value: 10 },
                { name: '15 minutes', value: 15 },
                { name: '20 minutes', value: 20 },
                { name: '30 minutes', value: 30 },
              ],
              displayOptions: {
                show: {
                  mode: ['everyXMinutes'],
                },
              },
            },
            // Every X Hours
            {
              key: 'hourInterval',
              label: 'Every',
              type: 'options',
              default: 2,
              description: 'Trigger every X hours',
              options: [
                { name: '2 hours', value: 2 },
                { name: '3 hours', value: 3 },
                { name: '4 hours', value: 4 },
                { name: '6 hours', value: 6 },
                { name: '12 hours', value: 12 },
              ],
              displayOptions: {
                show: {
                  mode: ['everyXHours'],
                },
              },
            },
            {
              key: 'startHour',
              label: 'Starting At Hour',
              type: 'number',
              default: 0,
              description: 'Starting hour for X hour intervals (0-23)',
              displayOptions: {
                show: {
                  mode: ['everyXHours'],
                },
              },
              typeOptions: {
                minValue: 0,
                maxValue: 23,
              },
            },
            // Minute field (used by multiple modes)
            {
              key: 'minute',
              label: 'Minute',
              type: 'number',
              default: 0,
              description: 'Minute (0-59)',
              displayOptions: {
                show: {
                  mode: ['everyHour', 'everyDay', 'everyWeek', 'everyMonth'],
                },
              },
              typeOptions: {
                minValue: 0,
                maxValue: 59,
              },
            },
            // Hour field (used by multiple modes)
            {
              key: 'hour',
              label: 'Hour',
              type: 'number',
              default: 9,
              description: 'Hour (0-23)',
              displayOptions: {
                show: {
                  mode: ['everyDay', 'everyWeek', 'everyMonth'],
                },
              },
              typeOptions: {
                minValue: 0,
                maxValue: 23,
              },
            },
            // Time range restrictions
            {
              key: 'restrictToTimeRange',
              label: 'Restrict to Time Range',
              type: 'boolean',
              default: false,
              description: 'Only trigger during specific hours of the day',
              displayOptions: {
                show: {
                  mode: ['everyXMinutes', 'everyHour', 'everyXHours'],
                },
              },
            },
            {
              key: 'timeRangeStart',
              label: 'From Hour',
              type: 'number',
              default: 9,
              description: 'Start hour (0-23)',
              displayOptions: {
                show: {
                  mode: ['everyXMinutes', 'everyHour', 'everyXHours'],
                  restrictToTimeRange: [true],
                },
              },
              typeOptions: {
                minValue: 0,
                maxValue: 23,
              },
            },
            {
              key: 'timeRangeEnd',
              label: 'To Hour',
              type: 'number',
              default: 17,
              description: 'End hour (0-23)',
              displayOptions: {
                show: {
                  mode: ['everyXMinutes', 'everyHour', 'everyXHours'],
                  restrictToTimeRange: [true],
                },
              },
              typeOptions: {
                minValue: 0,
                maxValue: 23,
              },
            },
            // Day of week restrictions
            {
              key: 'restrictToDays',
              label: 'Restrict to Days',
              type: 'boolean',
              default: false,
              description: 'Only trigger on specific days of the week',
              displayOptions: {
                show: {
                  mode: ['everyXMinutes', 'everyHour', 'everyXHours', 'everyDay'],
                },
              },
            },
            {
              key: 'daysOfWeek',
              label: 'Days of Week',
              type: 'multiOptions',
              default: [1, 2, 3, 4, 5],
              description: 'Days of the week to trigger',
              displayOptions: {
                show: {
                  mode: ['everyXMinutes', 'everyHour', 'everyXHours', 'everyDay'],
                  restrictToDays: [true],
                },
              },
              options: [
                { name: 'Sunday', value: 0 },
                { name: 'Monday', value: 1 },
                { name: 'Tuesday', value: 2 },
                { name: 'Wednesday', value: 3 },
                { name: 'Thursday', value: 4 },
                { name: 'Friday', value: 5 },
                { name: 'Saturday', value: 6 },
              ],
            },
            // Specific day for weekly/monthly
            {
              key: 'dayOfWeek',
              label: 'Day of Week',
              type: 'options',
              default: 1,
              description: 'Day of the week',
              displayOptions: {
                show: {
                  mode: ['everyWeek'],
                },
              },
              options: [
                { name: 'Sunday', value: 0 },
                { name: 'Monday', value: 1 },
                { name: 'Tuesday', value: 2 },
                { name: 'Wednesday', value: 3 },
                { name: 'Thursday', value: 4 },
                { name: 'Friday', value: 5 },
                { name: 'Saturday', value: 6 },
              ],
            },
            {
              key: 'dayOfMonth',
              label: 'Day of Month',
              type: 'number',
              default: 1,
              description: 'Day of the month (1-31)',
              displayOptions: {
                show: {
                  mode: ['everyMonth'],
                },
              },
              typeOptions: {
                minValue: 1,
                maxValue: 31,
              },
            },
            // Custom cron
            {
              key: 'cronExpression',
              label: 'Cron Expression',
              type: 'string',
              default: '0 0 * * *',
              placeholder: '0 0 * * *',
              description: 'Custom cron expression (minute hour day month dayOfWeek)',
              displayOptions: {
                show: {
                  mode: ['custom'],
                },
              },
              validation: [
                {
                  type: 'pattern',
                  pattern: '^(\\*|([0-9]|1[0-9]|2[0-9]|3[0-9]|4[0-9]|5[0-9])|\\*/[0-9]+)\\s+(\\*|([0-9]|1[0-9]|2[0-3])|\\*/[0-9]+)\\s+(\\*|([1-9]|1[0-9]|2[0-9]|3[0-1])|\\*/[0-9]+)\\s+(\\*|([1-9]|1[0-2])|\\*/[0-9]+)\\s+(\\*|[0-6]|\\*/[0-9]+)$',
                  message: 'Please enter a valid cron expression (minute hour day month dayOfWeek)',
                },
              ],
            },
          ],
        },
      ],
    },
    {
      key: 'timezone',
      label: 'Timezone',
      type: 'options',
      default: 'UTC',
      description: 'Timezone for the schedule',
      options: [
        { value: 'UTC', name: 'UTC' },
        { value: 'America/New_York', name: 'Eastern Time (US)' },
        { value: 'America/Chicago', name: 'Central Time (US)' },
        { value: 'America/Denver', name: 'Mountain Time (US)' },
        { value: 'America/Los_Angeles', name: 'Pacific Time (US)' },
        { value: 'America/Toronto', name: 'Toronto' },
        { value: 'America/Vancouver', name: 'Vancouver' },
        { value: 'Europe/London', name: 'London' },
        { value: 'Europe/Paris', name: 'Paris' },
        { value: 'Europe/Berlin', name: 'Berlin' },
        { value: 'Asia/Tokyo', name: 'Tokyo' },
        { value: 'Asia/Shanghai', name: 'Shanghai' },
        { value: 'Asia/Singapore', name: 'Singapore' },
        { value: 'Australia/Sydney', name: 'Sydney' },
      ],
    },
  ],
};

export default schema;
